import { apiFetch } from '../../utils/apiClient';
import type {
  LscAdminSupportIssue,
  LscSupportIssue,
  LscSupportIssueCategory,
  LscSupportIssueStatus,
} from '../../lsc/types';

export type RttsSupportCreateBody = {
  category: LscSupportIssueCategory;
  title: string;
  description: string;
  severity?: string;
  surface?: string;
  context?: Record<string, string | undefined>;
};

async function readJson(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function createRttsSupportIssue(
  payload: RttsSupportCreateBody
): Promise<LscSupportIssue> {
  const response = await apiFetch('/api/rtts/support/issues', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      severity: 'high',
      surface: 'studio',
      ...payload,
    }),
  });
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not submit report'));
  }
  return body.issue as LscSupportIssue;
}

export async function listMyRttsSupportIssues(): Promise<LscSupportIssue[]> {
  const response = await apiFetch('/api/rtts/support/issues/mine');
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not load reports'));
  }
  return (body.issues as LscSupportIssue[]) || [];
}

export async function listAdminRttsSupportIssues(
  view: 'open' | 'archived' | 'deleted' = 'open'
): Promise<LscAdminSupportIssue[]> {
  const response = await apiFetch(
    `/api/rtts/admin/support/issues?view=${encodeURIComponent(view)}`
  );
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not load inbox'));
  }
  return (body.issues as LscAdminSupportIssue[]) || [];
}

export async function getAdminRttsSupportIssue(issueId: string): Promise<LscAdminSupportIssue> {
  const response = await apiFetch(`/api/rtts/admin/support/issues/${encodeURIComponent(issueId)}`);
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Issue not found'));
  }
  return body.issue as LscAdminSupportIssue;
}

export async function updateAdminRttsSupportIssue(
  issueId: string,
  patch: { status?: LscSupportIssueStatus; admin_notes?: string; public_notes?: string }
): Promise<LscAdminSupportIssue> {
  const response = await apiFetch(`/api/rtts/admin/support/issues/${encodeURIComponent(issueId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not update issue'));
  }
  return body.issue as LscAdminSupportIssue;
}

export async function remediateRttsSupportIssue(
  issueId: string,
  action: 'grant_credits' | 'restore_free',
  amount: number
): Promise<{
  issue: LscAdminSupportIssue;
  reel_credit_balance: number;
  reels_remaining: number;
  idempotent: boolean;
}> {
  const response = await apiFetch(
    `/api/rtts/admin/support/${encodeURIComponent(issueId)}/remediate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, amount }),
    }
  );
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Remediation failed'));
  }
  return {
    issue: body.issue as LscAdminSupportIssue,
    reel_credit_balance: Number(body.reel_credit_balance || 0),
    reels_remaining: Number(body.reels_remaining || 0),
    idempotent: Boolean(body.idempotent),
  };
}

export async function getMyRttsSupportIssue(issueId: string): Promise<LscSupportIssue> {
  const response = await apiFetch(`/api/rtts/support/issues/${encodeURIComponent(issueId)}`);
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Issue not found'));
  }
  return body.issue as LscSupportIssue;
}

export async function postMyRttsSupportMessage(
  issueId: string,
  bodyText: string
): Promise<LscSupportIssue> {
  const response = await apiFetch(
    `/api/rtts/support/issues/${encodeURIComponent(issueId)}/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: bodyText }),
    }
  );
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not send reply'));
  }
  return (body.issue as LscSupportIssue) || (body as unknown as LscSupportIssue);
}

export async function postAdminRttsSupportMessage(
  issueId: string,
  bodyText: string
): Promise<LscAdminSupportIssue> {
  const response = await apiFetch(
    `/api/rtts/admin/support/${encodeURIComponent(issueId)}/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: bodyText }),
    }
  );
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not send reply'));
  }
  return body.issue as LscAdminSupportIssue;
}

export async function deleteAdminRttsSupportIssue(
  issueId: string
): Promise<LscAdminSupportIssue> {
  const response = await apiFetch(
    `/api/rtts/admin/support/${encodeURIComponent(issueId)}/delete`,
    { method: 'POST' }
  );
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not delete issue'));
  }
  return body.issue as LscAdminSupportIssue;
}

export async function restoreAdminRttsSupportIssue(
  issueId: string
): Promise<LscAdminSupportIssue> {
  const response = await apiFetch(
    `/api/rtts/admin/support/${encodeURIComponent(issueId)}/restore`,
    { method: 'POST' }
  );
  const body = await readJson(response);
  if (!response.ok) {
    throw new Error(String(body.message || body.error || 'Could not restore issue'));
  }
  return body.issue as LscAdminSupportIssue;
}
